import {useRef, useState} from "react";
import styled from "styled-components";
import {track} from "analytics";
import {guestErrorCode, guestErrorMessage} from "api/guest.api";
import {TURNSTILE_SITE_KEY} from "conts/features";
import {bookingErrorMessage, buildBookingPayload, useBookingRequest} from "pages/user/PaymentPage/hooks/useBookingRequest";
import {Turnstile} from "ui/base/turnstile/Turnstile";
import {LoginModal} from "ui/blocks/AuthModals/loginModal/LoginModal";
import {ForgotPasswordModal} from "ui/blocks/AuthModals/forgotPasswordModal/forgotPasswordModal";
import {Alert, AlertIcon, LockIcon, Muted, PillButton, Spinner, tokens} from "ui/blocks/guestMessaging/GuestUI";
import {CodeEntry} from "ui/blocks/quickBooking/CodeEntry";
import {CodeLogin} from "ui/blocks/quickBooking/CodeLogin";
import {GuestDetailsFields, validateGuestDetails} from "ui/blocks/quickBooking/GuestDetailsFields";
import {useQuickBookingAuth} from "ui/blocks/quickBooking/useQuickBookingAuth";
import {Agreement, CardField} from "./CheckoutParts";

/**
 * Checkout for a new student: name, email, phone and card, then the 4-digit
 * code. After the code: account created and signed in, card saved, booking
 * request sent (same request as signed-in students).
 *
 * collectCard: false when no publishable key is available before sign-in;
 * the card is then entered after the code, in the signed-in checkout.
 */
export const GuestCheckout = ({
    teacherId, bookingInput, cancellation, instructorName, disabled, collectCard,
    onCodeSent, onBackToForm, onBooked, onSignedInButNotBooked,
}) => {
    const {sendCode, verify} = useQuickBookingAuth(teacherId);
    const {createCardPaymentMethod, requestBooking} = useBookingRequest();

    const [values, setValues] = useState({name: "", email: "", phone: ""});
    const [errors, setErrors] = useState({});
    const [card, setCard] = useState({complete: false, error: ""});
    const [formError, setFormError] = useState("");
    const [accountExists, setAccountExists] = useState(false);
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState("form"); // form | code | booking
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileReset, setTurnstileReset] = useState(0);
    const [showLogin, setShowLogin] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const paymentMethodRef = useRef(null);
    const busyRef = useRef(false);

    const needsBotCheck = Boolean(TURNSTILE_SITE_KEY);

    const onChange = (field, value) => {
        setValues((prev) => ({...prev, [field]: value}));
        if (errors[field]) setErrors((prev) => ({...prev, [field]: ""}));
        setFormError("");
        setAccountExists(false);
        paymentMethodRef.current = null;
    };

    // Sends (or resends) the code. Returns true when a code is on its way.
    const send = async () => {
        try {
            await sendCode(values, turnstileToken);
            return true;
        } catch (error) {
            const code = guestErrorCode(error);
            track("quick_booking_code_failed", {teacher_id: teacherId, error: code || "network", popup: true});
            if (code === "too_soon") return true;
            if (code === "account_exists") {
                setAccountExists(true);
                setPhase("form");
                onBackToForm();
            } else {
                setFormError(guestErrorMessage(error, "We couldn't send the code. Check your connection and try again."));
            }
            return false;
        } finally {
            setTurnstileReset((n) => n + 1); // Turnstile tokens are single-use
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (busyRef.current || disabled) return;
        const next = validateGuestDetails(values);
        if (collectCard && !card.complete) next.card = card.error || "Enter your card details.";
        setErrors(next);
        if (Object.keys(next).length || (needsBotCheck && !turnstileToken)) return;

        busyRef.current = true;
        setBusy(true);
        setFormError("");
        track("quick_booking_book_clicked", {teacher_id: teacherId, popup: true});
        try {
            // Check the card with Stripe first, so a typo shows before the code is sent.
            if (collectCard && !paymentMethodRef.current) {
                paymentMethodRef.current = await createCardPaymentMethod();
            }
            if (await send()) {
                setPhase("code");
                onCodeSent();
            }
        } catch (error) {
            setErrors((prev) => ({...prev, card: bookingErrorMessage(error, "Check your card details and try again.")}));
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };

    // Code accepted -> signed in -> booking request. Code errors are shown by CodeEntry.
    const onVerify = async (code) => {
        await verify(values, code);
        if (!collectCard || !paymentMethodRef.current) {
            onSignedInButNotBooked("");
            return;
        }
        setPhase("booking");
        try {
            const booking = await requestBooking({
                payload: buildBookingPayload(bookingInput),
                cardId: paymentMethodRef.current,
                isNewCard: true,
            });
            onBooked(booking);
        } catch (error) {
            // Account is ready and signed in; let them retry with the signed-in checkout.
            onSignedInButNotBooked(bookingErrorMessage(error));
        }
    };

    const loginModals = (
        <>
            {showLogin && (
                <LoginModal
                    show={showLogin}
                    handleClose={() => setShowLogin(false)}
                    setSignupModal={() => setShowLogin(false)}
                    setForgotPasswordModal={setShowForgot}
                />
            )}
            {showForgot && (
                <ForgotPasswordModal show={showForgot} handleClose={() => setShowForgot(false)} setSigninModal={setShowLogin}/>
            )}
        </>
    );

    if (phase === "code") {
        return (
            <Section>
                <SectionTitle>Confirm your booking</SectionTitle>
                <CodeEntry
                    phone={values.phone}
                    onVerify={onVerify}
                    onResend={send}
                    onChangeNumber={() => { setPhase("form"); onBackToForm(); }}
                />
            </Section>
        );
    }
    // Existing account (no password needed): log in with a text code, then the
    // popup continues to payment with the same selection.
    if (accountExists) {
        return (
            <Section>
                <div>
                    <SectionTitle>Welcome back</SectionTitle>
                    <Hint>You already have a Teache account. Log in to continue booking. Your selected times are kept.</Hint>
                </div>
                <CodeLogin
                    initialPhone={values.phone}
                    initialEmail={values.email}
                    defaultMethod="phone"
                    source="booking_popup"
                    idPrefix="bp-login"
                    onUsePassword={() => setShowLogin(true)}
                />
                <BackButton type="button" onClick={() => setAccountExists(false)}>Back to your details</BackButton>
                {loginModals}
            </Section>
        );
    }
    if (phase === "booking") {
        return (
            <Section aria-busy="true">
                <Sending><Spinner $size={20}/> Sending your request to {instructorName}…</Sending>
            </Section>
        );
    }

    return (
        <Section as="form" onSubmit={handleSubmit} noValidate aria-busy={busy}>
            <div>
                <SectionTitle>Your details</SectionTitle>
                <Hint>We'll text you a 4-digit code to confirm your booking.</Hint>
            </div>
            <GuestDetailsFields values={values} errors={errors} onChange={onChange} idPrefix="bp" disabled={busy}/>
            {collectCard && (
                <CardField
                    error={errors.card}
                    disabled={busy}
                    onChange={(c) => {
                        setCard(c);
                        paymentMethodRef.current = null;
                        if (errors.card) setErrors((prev) => ({...prev, card: ""}));
                    }}
                />
            )}

            {needsBotCheck && (
                <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetSignal={turnstileReset}/>
            )}

            {formError && <Alert role="alert"><AlertIcon/><span>{formError}</span></Alert>}

            <Agreement cancellation={cancellation} instructorName={instructorName}/>

            <PillButton type="submit" $block disabled={busy || disabled || (needsBotCheck && !turnstileToken)}>
                {busy ? <><Spinner $light $size={16}/> Checking…</> : "Book Class"}
            </PillButton>
            <Muted>
                <LockIcon size={14}/>
                <span>Your email and phone stay private. Instructors never see them.</span>
            </Muted>

        </Section>
    );
};

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 4px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: ${tokens.ink};
`;

const Hint = styled.p`
    margin: 2px 0 0;
    font-size: 13px;
    color: ${tokens.muted};
`;

const BackButton = styled.button`
    align-self: center;
    background: none;
    border: 0;
    padding: 4px;
    font: inherit;
    font-size: 14px;
    color: ${tokens.muted};
    cursor: pointer;
`;

const Sending = styled.p`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 8px 0;
    font-size: 15px;
    color: ${tokens.text};
`;
